import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CommentRootModel } from "@repo/structure";
import {
  getTemplateEditionDataQuery,
  checkStructureVersionQuery,
  getAllLabelsQuery,
  getCompanyRateCardQuery,
} from "@query";
import {
  PROJECT_COMPANY_SETTINGS,
  USER_COMPANY_SETTINGS,
  COMPANY_SETTINGS,
  TemplateSocketProvider,
} from "@client";
import { LabelGroups, parseCompanySettings, PROJECT_TYPE } from "@utils";
import { templates } from "@paths";
import { useStores } from "@hooks";
import {
  EstimateEditorStore,
  EstimateEditorStoreProvider,
  EstimateLoadingAlert,
  EstimateEditorInactivityMonitor,
  EstimateEditorConnectionErrorAlert,
  ESTIMATE_ERROR_CODES,
  EstimateCommentsProvider,
} from "@tools";
import { NewEditorVersionAlert } from "@dialogs";
import { PageCircularProgress } from "@components";
import { TemplateEditorHandlers } from "./TemplateEditorHandlers";

export const TemplateEditor = () => {
  const {t} = useTranslation();
  const { userStore, stateStore } = useStores();
  const navigate = useNavigate();

  const { estimateUuid } = useParams();

  const [ store, setStore ] = useState(null);
  const [ commentStore, setCommentStore ] = useState(null);
  const [ commentHash, setCommentHash ] = useState(null);
  const [ errorCode, setErrorCode ] = useState(null);
  const [ hasNewEditorVersion, setHasNewEditorVersion ] = useState(false);
  
  useEffect(() => {
    document.title = `${t("routes.editor_template")} - Apropo`;
  }, [])

  useEffect(() => {
    setupEditorStore();
    return () => {
      stateStore.resetFilesUploadProgress();
    }
  }, []);

  const loadTemplateData = async () => {
    await checkStructureVersionQuery();
    
    try {
      const {
        hash,
        companySettings,
        templateData,
      } = await getTemplateEditionDataQuery(estimateUuid, /*userStore.userUuid,*/ [
        USER_COMPANY_SETTINGS.AVERAGE_ALERT(userStore.userUuid),
        USER_COMPANY_SETTINGS.HIDE_TIMELINE_SECTION_ALERT(userStore.userUuid),
        USER_COMPANY_SETTINGS.RESIZE_ALERT(userStore.userUuid),
        USER_COMPANY_SETTINGS.RISK_ALERT(userStore.userUuid),
        PROJECT_COMPANY_SETTINGS.VALUE_ALERT(`t-${estimateUuid}`),
        PROJECT_COMPANY_SETTINGS.VALUE_PROJECT(`t-${estimateUuid}`),
        COMPANY_SETTINGS.COMPANY_CURRENCY,
        COMPANY_SETTINGS.CURRENCY_DEF,
        COMPANY_SETTINGS.TIME_EQUIV,
        COMPANY_SETTINGS.COMPANY_CURRENCY,
        COMPANY_SETTINGS.CURRENCY_DEF,
      ]);
      
      if(templateData.name)
        document.title = `${templateData.name} - Apropo`;
      
      // const groupedComments = await fetchGroupedTemplateVersionCommentsQuery(templateId, userStore.userUuid);
      
      const companyLabels = await getAllLabelsQuery(LabelGroups.LABELS);
      const customLabels = await getAllLabelsQuery(estimateUuid);
      const workTypes = await getCompanyRateCardQuery();
      
      const timeEquiv = parseCompanySettings(companySettings[COMPANY_SETTINGS.TIME_EQUIV]);
      companySettings.dayModifier = timeEquiv?.day;
      
      return {
        hash,
        companySettings,
        companyLabels,
        customLabels,
        workTypes,
        estimateData: templateData,
      }
    } catch(e) {
      if(e.status === 403)
        navigate(templates, { replace: true });
      else throw e;
    }
  };
  
  const reloadTemplateData = async () => {
    const { hash, estimateData, companySettings } = await loadTemplateData();
    setCommentHash(hash);
    store.setCompanySettings(companySettings);
    store.setEstimateData({ estimateData, keepStructures: true });
    return { hash, estimateData };
  };
  
  const setupEditorStore = async () => {
    try {
      const { hash, groupedComments, ...data } = await loadTemplateData();
      
      setCommentHash(hash);
      setCommentStore(CommentRootModel.create({
        commenterData: {
          uuid: userStore.data.uuid,
          fullName: userStore.data.fullname,
          color: userStore.data.avatarColor
        },
        groups: groupedComments,
        // useInternalComments: false,
        // useExternalComments: true,
        useInternalComments: true,
        useExternalComments: false,
      }))
      setStore(new EstimateEditorStore({
        useHistoryManager: true,
        estimateType: PROJECT_TYPE.STANDARD,
        ...data
      }));
    } catch (e) {
      console.log("RELOAD:", e.reload);
      if(e.reload)
        setHasNewEditorVersion(true);
      else if(e.status === 500 && e.response?.data?.error?.[0] === "Record Not Exist")
        setErrorCode(ESTIMATE_ERROR_CODES.TEMPLATE_DELETED);
      else
        setErrorCode(ESTIMATE_ERROR_CODES.TEMPLATE_UNKNOWN);
    }
  };
  
  if (errorCode)
    return <EstimateLoadingAlert code={errorCode} />;

  return <>
    {
      !store ? (
        <PageCircularProgress />
      ) : (
        <EstimateCommentsProvider value={commentStore}>
          <EstimateEditorStoreProvider value={store}>
            <TemplateSocketProvider reloadTemplateData={reloadTemplateData}>
              <TemplateEditorHandlers commentHash={commentHash} />
              <EstimateEditorInactivityMonitor reloadEditorData={reloadTemplateData} />
              <EstimateEditorConnectionErrorAlert />
            </TemplateSocketProvider>
          </EstimateEditorStoreProvider>
        </EstimateCommentsProvider>
      )
    }
    {
      hasNewEditorVersion &&
      <NewEditorVersionAlert />
    }
  </>
};